import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { catchError, map, of } from 'rxjs';

import { PortalQueryParams, PortalRoutes } from '@app/app-routing.module';
import { PortalOrderNode } from '@app/services/api/api.types';
import { ApiProtectedService } from '@app/services/api/api.protected.service';
import { PortalLeadService } from '@app/services/portal-lead/portal-lead.service';
import { PortalOrderService } from '@app/services/portal-order/portal-order.service';
import { StateService } from '@app/services/state/state.service';
import {
  patchCheckoutStateFromUrl,
  urlRequestsBundleRestore,
} from '@app/utils/checkout-resume.util';

/**
 * After login, `PortalOrderPageGuard` requires `portalOrder` in memory. We load the option (or
 * upsell trip) via `portalAuthorizedOrder` before navigating so the guard passes.
 */
@Injectable({
  providedIn: 'root',
})
export class CheckoutResumeService {
  apiProtectedService = inject(ApiProtectedService);
  portalOrderService = inject(PortalOrderService);
  portalLeadService = inject(PortalLeadService);
  stateService = inject(StateService);
  router = inject(Router);

  /**
   * Reads resume params from the current window URL, restores checkout state, loads portal order,
   * then navigates to the checkout step. Returns whether a resume navigation was performed.
   */
  async resumeAfterLogin(): Promise<boolean> {
    const url = new URL(window.location.href);

    patchCheckoutStateFromUrl(url, this.stateService);

    const resume = url.searchParams.get(PortalQueryParams.CheckoutResume);

    if (!resume) {
      return false;
    }

    const pathKind =
      (url.searchParams.get(PortalQueryParams.CheckoutPath) as 'option' | 'upsell' | null) ??
      'option';

    if (pathKind === 'upsell') {
      return this.resumeUpsellPath(url, resume);
    }

    return this.resumeLeadOptionPath(url, resume);
  }

  async resumeUpsellPath(url: URL, resume: string): Promise<boolean> {
    const upsellTrip = url.searchParams.get(PortalQueryParams.UpsellTripPortalLink);

    if (!upsellTrip) {
      return false;
    }

    return this.loadOrderAndNavigateToCheckout(url, upsellTrip, [
      PortalRoutes.AncillaryUpsell,
      upsellTrip,
      resume,
    ]);
  }

  async resumeLeadOptionPath(url: URL, resume: string): Promise<boolean> {
    const lead = url.searchParams.get('portalLink');
    const option = url.searchParams.get('option');

    if (!lead || !option) {
      return false;
    }

    this.portalLeadService.getPortalLead({ portalLink: lead });

    return this.loadOrderAndNavigateToCheckout(url, option, [lead, option, resume]);
  }

  /** Loads `portalAuthorizedOrder`, applies bundle restore, navigates to the resume segment. */
  async loadOrderAndNavigateToCheckout(
    url: URL,
    optionPortalLink: string,
    commands: (string | undefined)[],
  ): Promise<boolean> {
    const order = await this.fetchAuthorizedOrder(optionPortalLink);

    if (!order) {
      return false;
    }

    this.portalOrderService.setPortalOrder(order);
    this.applyBundleFromOrderAfterLogin(url, order);

    await this.router.navigate(commands, { replaceUrl: true });

    return true;
  }

  /**
   * When `bundle=1` is in the redirect URL, `patchCheckoutStateFromUrl` only sets
   * `bundleRestorePending`. After the authorized order loads, attach the real bundle from the
   * order so Travelers / overview pricing matches the bundled ancillary product.
   */
  applyBundleFromOrderAfterLogin(url: URL, order: PortalOrderNode) {
    if (!urlRequestsBundleRestore(url) || !order.ancillaryBundle) {
      return;
    }

    this.stateService.patchState({
      bundle: order.ancillaryBundle,
      bundleRestorePending: undefined,
      disruptionProtection: undefined,
      cancelForAnyReason: undefined,
    });
  }

  async fetchAuthorizedOrder(portalLink: string): Promise<PortalOrderNode | null> {
    return firstValueFrom(
      this.apiProtectedService.portalAuthorizedOrder(portalLink).pipe(
        map((response) => {
          if (response.errors?.length) {
            return null;
          }

          return response.data?.portalAuthorizedOrder?.order ?? null;
        }),
        catchError(() => of(null)),
      ),
    );
  }
}
